import Validator from 'validatorjs';
import { SUPPRESSION_TYPES } from '@kentik/ui-shared/alerting/constants';

import alarmSuppressionFields from 'app/forms/config/synthetics/fields/alarmSuppressionFields';

const { startTime, expirationTime, neverExpire } = alarmSuppressionFields;

// This validation only applies to condition types
function noDuplicateDimensions(value) {
  const { type } = this.validator.input || {};
  const typesToValidate = [SUPPRESSION_TYPES.CONDITION, SUPPRESSION_TYPES.METRIC, SUPPRESSION_TYPES.NMS];

  if (!typesToValidate.includes(type)) {
    return true;
  }

  const dimensions = value.map(({ filterField }) => filterField);

  return dimensions.length === new Set(dimensions).size;
}

Validator.register(
  'noDuplicateDimensions',
  noDuplicateDimensions,
  'Each condition dimension can be used only once in an Alert Suppression'
);

export const options = {
  name: 'Alert Suppression'
};

export const FILTER_OPERATOR_OPTIONS = [
  { label: 'equals', value: 'equals' },
  { label: 'does not equal', value: 'not_equals' },
  { label: 'starts with', value: 'starts_with' },
  { label: 'does not start with', value: 'not_starts_with' },
  { label: 'ends with', value: 'ends_with' },
  { label: 'does not end with', value: 'not_ends_with' },
  { label: 'contains', value: 'contains' },
  { label: 'does not contain', value: 'not_contains' },
  { label: 'is empty', value: 'empty' },
  { label: 'is present', value: 'any' }
];

// Used when building the filter payload to translate the selected value to { key, not } format expected by the API
export const CONDITION_OPERATOR_MAP = {
  equals: { key: 'equals', not: false },
  not_equals: { key: 'equals', not: true },
  starts_with: { key: 'starts_with', not: false },
  not_starts_with: { key: 'starts_with', not: true },
  ends_with: { key: 'ends_with', not: false },
  not_ends_with: { key: 'ends_with', not: true },
  contains: { key: 'contains', not: false },
  not_contains: { key: 'contains', not: true },
  empty: { key: 'empty', not: false },
  any: { key: 'empty', not: true }
};

export const BOOLEAN_CONDITION_OPERATORS = ['empty', 'any'];

// For loading an existing suppression, so that the correct operator shows as selected in the UI when a suppression is being edited
export const getFilterOperator = (value) => {
  const not = value.not === true;
  if ('equals' in value) {
    return not ? 'not_equals' : 'equals';
  }
  if ('starts_with' in value) {
    return not ? 'not_starts_with' : 'starts_with';
  }
  if ('ends_with' in value) {
    return not ? 'not_ends_with' : 'ends_with';
  }
  if ('contains' in value) {
    return not ? 'not_contains' : 'contains';
  }
  if ('empty' in value) {
    return not ? 'any' : 'empty';
  }
  if ('any' in value) {
    return 'any';
  }
  return 'equals';
};

export const buildKeyValueFilters = (filters) =>
  filters.map(({ filterField, filterOperator = 'equals', filterValue }) => {
    const { key, not } = CONDITION_OPERATOR_MAP[filterOperator] ?? CONDITION_OPERATOR_MAP.equals;
    const isBooleanOperator = BOOLEAN_CONDITION_OPERATORS.includes(key);

    if (!isBooleanOperator && filterValue === undefined) {
      throw new Error(`filterValue is required for "${filterOperator}`);
    }

    return {
      key: { equals: filterField },
      value: { ...(not && { not: true }), [key]: isBooleanOperator ? true : filterValue }
    };
  });

export const fields = {
  type: {
    label: 'Pattern Type',
    defaultValue: SUPPRESSION_TYPES.CONDITION,
    options: [
      {
        value: SUPPRESSION_TYPES.CONDITION,
        label: 'Match Conditions'
      },
      {
        value: SUPPRESSION_TYPES.NMS,
        label: 'Match NMS Conditions'
      },
      {
        value: SUPPRESSION_TYPES.METRIC,
        label: 'Match NMS Conditions (Classic Threshold)'
      },
      {
        value: SUPPRESSION_TYPES.POLICY,
        label: 'Match Alert Policy'
      },
      {
        value: SUPPRESSION_TYPES.SYNTH,
        label: 'Match Test'
      },
      {
        value: SUPPRESSION_TYPES.AGENT,
        label: 'Match Agent'
      }
    ]
  },
  startTime,
  expirationTime,
  neverExpire,
  comment: {
    label: 'Comment',
    placeholder: 'Enter comment...'
  },
  conditionsGroup: {
    isComplexArray: true,
    label: 'Condition Group'
  },
  'conditionsGroup[].connector': {
    defaultValue: 'partial',
    options: [
      {
        label: 'Strict Dimension Match',
        value: 'full'
      },
      {
        label: 'Partial Dimension Match',
        value: 'partial'
      }
    ]
  },
  'conditionsGroup[].filters': {
    defaultValue: [],
    label: 'Condition Group',
    isComplexArray: true,
    rules: [
      { required_if: ['type', SUPPRESSION_TYPES.CONDITION] },
      { required_if: ['type', SUPPRESSION_TYPES.METRIC] },
      { required_if: ['type', SUPPRESSION_TYPES.NMS] },
      'noDuplicateDimensions'
    ],
    messages: {
      required_if: 'A condition is required'
    }
  },
  'conditionsGroup[].filters[].filterField': {
    rules: 'required|noConflictingUniques',
    label: 'field'
  },
  'conditionsGroup[].filters[].filterOperator': {
    defaultValue: 'equals',
    options: FILTER_OPERATOR_OPTIONS
  },
  'conditionsGroup[].filters[].filterValue': {
    placeholder: 'Enter value...',
    label: 'value',
    messages: {
      required: 'A condition value is required'
    }
  },
  ruleId: {
    label: SUPPRESSION_TYPES.POLICY,
    rules: [{ required_if: ['type', SUPPRESSION_TYPES.POLICY] }],
    messages: {
      required_if: 'A policy is required'
    }
  },
  testIds: {
    defaultValue: [],
    rules: [{ required_if: ['type', SUPPRESSION_TYPES.SYNTH] }],
    messages: {
      required_if: 'Please select one or more tests'
    }
  },
  agentIds: {
    defaultValue: [],
    rules: [{ required_if: ['type', SUPPRESSION_TYPES.AGENT] }],
    messages: {
      required_if: 'Please select one or more agents'
    }
  },
  measurement: {
    label: 'Measurement',
    rules: [{ required_if: ['type', SUPPRESSION_TYPES.NMS] }],
    messages: {
      required_if: 'Please select a measurement'
    }
  }
};
